import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Lock,
  RefreshCw,
  Phone,
  Mail,
  CheckCircle2,
  XCircle,
  Copy,
  LogOut,
  Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";
import Seo from "@/components/site/Seo";

const STORAGE_KEY = "procraft_admin_key";

function LoginGate({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/verify`,
        {},
        { headers: { "X-Admin-Key": pw } }
      );
      localStorage.setItem(STORAGE_KEY, pw);
      onSuccess(pw);
    } catch {
      toast.error("Wrong admin key.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-5">
      <motion.form
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-[#e7e5df] rounded-3xl p-8 shadow-sm"
        data-testid="admin-login-form"
      >
        <div className="h-12 w-12 rounded-2xl bg-[#16a34a]/10 text-[#16a34a] inline-flex items-center justify-center">
          <Lock className="w-6 h-6" />
        </div>
        <h1 className="font-display font-black text-3xl text-[#1c1917] mt-4 tracking-tight">
          Lead Dashboard
        </h1>
        <p className="text-[#57534e] mt-2 text-sm">
          Enter the admin key to view every lead submitted through the site.
        </p>

        <div className="mt-6">
          <Input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Admin key"
            className="h-12 rounded-xl"
            autoFocus
            data-testid="admin-key-input"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !pw}
          className="mt-5 w-full h-12 rounded-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold transition"
          data-testid="admin-login-submit"
        >
          {loading ? "Checking…" : "Unlock dashboard"}
        </button>
      </motion.form>
    </div>
  );
}

function StatCard({ label, value, tone = "default" }) {
  const tones = {
    default: "bg-white border-[#e7e5df] text-[#1c1917]",
    success: "bg-[#16a34a]/10 border-[#16a34a]/25 text-[#15803d]",
    warn: "bg-[#eab308]/10 border-[#eab308]/25 text-[#a16207]",
  };
  return (
    <div className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <div className="text-xs uppercase tracking-[0.18em] font-bold opacity-70">
        {label}
      </div>
      <div className="font-display font-black text-3xl mt-2">{value}</div>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function LeadRow({ lead }) {
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white border border-[#e7e5df] rounded-2xl p-5 md:p-6"
      data-testid={`lead-row-${lead.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-extrabold text-lg text-[#1c1917]">
              {lead.name}
            </h3>
            {lead.email_sent ? (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-[#16a34a]/12 text-[#15803d]">
                <CheckCircle2 className="w-3.5 h-3.5" /> Emailed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-[#eab308]/12 text-[#a16207]">
                <XCircle className="w-3.5 h-3.5" /> Email failed
              </span>
            )}
            {lead.service && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#f4f1ea] text-[#57534e]">
                {lead.service}
              </span>
            )}
          </div>
          <div className="text-xs text-[#57534e] mt-1">
            {formatDate(lead.created_at)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={`mailto:${lead.email}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 h-8 rounded-full bg-[#1c1917] text-white hover:bg-black transition"
            data-testid={`lead-email-${lead.id}`}
          >
            <Mail className="w-3.5 h-3.5" />
            {lead.email}
          </a>
          <button
            onClick={() => copy(lead.email, "Email")}
            className="h-8 w-8 rounded-full bg-[#f4f1ea] hover:bg-[#e7e5df] inline-flex items-center justify-center"
            aria-label="Copy email"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          {lead.phone && (
            <>
              <a
                href={`tel:${lead.phone}`}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 h-8 rounded-full bg-[#16a34a] text-white hover:bg-[#15803d] transition"
                data-testid={`lead-phone-${lead.id}`}
              >
                <Phone className="w-3.5 h-3.5" />
                {lead.phone}
              </a>
              <button
                onClick={() => copy(lead.phone, "Phone")}
                className="h-8 w-8 rounded-full bg-[#f4f1ea] hover:bg-[#e7e5df] inline-flex items-center justify-center"
                aria-label="Copy phone"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
      <p className="mt-4 text-[#1c1917] text-[15px] leading-relaxed whitespace-pre-wrap">
        {lead.message}
      </p>
    </motion.div>
  );
}

function Dashboard({ adminKey, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Key": adminKey };
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        onLogout();
      } else {
        toast.error("Could not load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey, onLogout]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#16a34a]">
              ProCraft Solutions
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl text-[#1c1917] mt-1 tracking-tight">
              Lead Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-white border border-[#e7e5df] font-bold text-sm hover:border-[#16a34a] hover:text-[#16a34a] transition"
              data-testid="admin-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 h-11 px-4 rounded-full border-2 border-[#1c1917] font-bold text-sm hover:bg-[#1c1917] hover:text-white transition"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" />
              Log out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-8">
          <StatCard label="Total leads" value={stats?.total_leads ?? "—"} />
          <StatCard
            label="Emails delivered"
            value={stats?.emails_delivered ?? "—"}
            tone="success"
          />
          <StatCard
            label="Email failures"
            value={stats?.emails_failed ?? "—"}
            tone="warn"
          />
        </div>

        <div className="mt-10 flex items-center justify-between">
          <h2 className="font-display font-extrabold text-xl text-[#1c1917]">
            Recent leads
          </h2>
          <span className="text-xs text-[#57534e]">
            Auto-refreshes every 30s
          </span>
        </div>

        <div className="mt-4 space-y-3">
          {leads.length === 0 && !loading && (
            <div className="bg-white border border-dashed border-[#e7e5df] rounded-2xl p-10 text-center">
              <div className="h-12 w-12 rounded-2xl bg-[#f4f1ea] inline-flex items-center justify-center text-[#57534e]">
                <Inbox className="w-6 h-6" />
              </div>
              <p className="mt-3 text-[#57534e]">
                No leads yet. New submissions will appear here in real time.
              </p>
            </div>
          )}
          {leads.map((lead) => (
            <LeadRow key={lead.id} lead={lead} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminLeads() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAdminKey("");
  };

  return (
    <>
      <Seo
        title="Lead Dashboard"
        description="Private ProCraft Solutions lead dashboard."
        path="/admin/leads"
      />
      {adminKey ? (
        <Dashboard adminKey={adminKey} onLogout={logout} />
      ) : (
        <LoginGate onSuccess={setAdminKey} />
      )}
    </>
  );
}
